import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/streamApi';
import PatrolZoneEditor from '../components/PatrolZoneEditor';

// Guard Patrol — configuration screen (Phase 1). Define routes → checkpoints
// (camera + drawn polygon zone + expected guard + gesture timing) → schedule →
// supervisors. The module is OFF until the master toggle is enabled.
// Reports live on a separate screen (added once the occurrence engine exists).

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // active_days: 0=Mon .. 6=Sun
const GESTURE_LABEL = { either_hand_raised: 'Raise either hand above shoulder' };

const blankCheckpoint = () => ({
  name: 'Checkpoint', camera_idx: '', expected_guard: '',
  patrol_zone: [], min_presence_seconds: 3, gesture: 'either_hand_raised', gesture_hold_seconds: 2,
  face_checkin: true,
});

const blankRoute = () => ({
  name: '', active: true, supervisor_override: [],
  schedule: { start: '22:00', end: '06:00', frequency_minutes: 30,
              early_window_seconds: 300, late_window_seconds: 300, active_days: [0, 1, 2, 3, 4, 5, 6] },
  checkpoints: [blankCheckpoint()],
});

export default function GuardPatrolPage() {
  const navigate = useNavigate();
  const [cfg, setCfg]         = useState(null);
  const [opts, setOpts]       = useState({ cameras: [], guards: [], supervisors: [] });
  const [draft, setDraft]     = useState(null);   // route being added/edited, or null
  const [editId, setEditId]   = useState(null);   // id of route being edited (null = new)
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([
        apiFetch('/ui/patrol/config').then(r => r.ok ? r.json() : null),
        apiFetch('/ui/patrol/form-options').then(r => r.ok ? r.json() : null),
      ]);
      if (c) setCfg(c);
      if (o) setOpts(o);
    } catch { setErr('Failed to load patrol config.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async patch => {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/ui/patrol/settings', { method: 'PUT', json: patch });
      if (r.ok) { const updated = await r.json(); setCfg(c => ({ ...c, ...updated })); }
      else setErr('Save failed.');
    } catch { setErr('Save failed.'); } finally { setBusy(false); }
  };

  const startNew  = () => { setEditId(null); setDraft(blankRoute()); };
  const startEdit = route => { setEditId(route.id); setDraft(JSON.parse(JSON.stringify(route))); };
  const cancel    = () => { setDraft(null); setEditId(null); setErr(''); };

  const saveRoute = async () => {
    if (!draft.name.trim()) { setErr('Route name is required.'); return; }
    for (const cp of draft.checkpoints) {
      if (cp.camera_idx === '' || cp.camera_idx === null) { setErr(`Checkpoint "${cp.name}" needs a camera.`); return; }
      if (!cp.patrol_zone || cp.patrol_zone.length < 3)   { setErr(`Checkpoint "${cp.name}" needs a zone of 3+ points.`); return; }
    }
    setBusy(true); setErr('');
    const body = { ...draft, checkpoints: draft.checkpoints.map(cp => ({ ...cp, camera_idx: Number(cp.camera_idx) })) };
    try {
      const r = editId
        ? await apiFetch(`/ui/patrol/routes/${editId}`, { method: 'PUT',  json: body })
        : await apiFetch('/ui/patrol/routes',            { method: 'POST', json: body });
      if (r.ok) { cancel(); await load(); } else setErr('Save failed.');
    } catch { setErr('Save failed.'); } finally { setBusy(false); }
  };

  const deleteRoute = async route => {
    if (!window.confirm(`Delete patrol route "${route.name}"?`)) return;
    setBusy(true);
    try { await apiFetch(`/ui/patrol/routes/${route.id}`, { method: 'DELETE' }); await load(); }
    finally { setBusy(false); }
  };

  // ── draft mutation helpers ──
  const setRoute = patch => setDraft(d => ({ ...d, ...patch }));
  const setSched = patch => setDraft(d => ({ ...d, schedule: { ...d.schedule, ...patch } }));
  const toggleDay = i => setDraft(d => {
    const days = d.schedule.active_days.includes(i)
      ? d.schedule.active_days.filter(x => x !== i)
      : [...d.schedule.active_days, i].sort((a, b) => a - b);
    return { ...d, schedule: { ...d.schedule, active_days: days } };
  });
  const setCp = (i, patch) => setDraft(d => ({ ...d, checkpoints: d.checkpoints.map((cp, j) => j === i ? { ...cp, ...patch } : cp) }));
  const addCp = () => setDraft(d => ({ ...d, checkpoints: [...d.checkpoints, blankCheckpoint()] }));
  const rmCp  = i => setDraft(d => ({ ...d, checkpoints: d.checkpoints.filter((_, j) => j !== i) }));
  const toggleSup = (list, id) => list.includes(id) ? list.filter(x => x !== id) : [...list, id];

  if (!cfg) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading Guard Patrol…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🛡️ Guard Patrol</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Turn CCTV cameras into virtual patrol checkpoints. A guard checks in with a raised-hand gesture; missed patrols notify supervisors.
      </p>
      {err && <div style={{ ...card, borderColor: 'var(--danger)', color: 'var(--danger)' }}>{err}</div>}

      {/* ── Master settings ── */}
      <section style={card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 16 }}>
          <input type="checkbox" checked={!!cfg.enabled} disabled={busy}
                 onChange={e => saveSettings({ enabled: e.target.checked })} />
          <b>Enable Guard Patrol</b>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{cfg.enabled ? 'Active' : 'Off — nothing runs until enabled'}</span>
        </label>

        <div style={{ marginTop: 16 }}>
          <div style={label}>Supervisors (default recipients for missed-patrol alerts)</div>
          {opts.supervisors.length === 0
            ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No web users available to select.</div>
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {opts.supervisors.map(u => (
                  <label key={u.id} style={chip(cfg.supervisors?.includes(u.id))}>
                    <input type="checkbox" checked={cfg.supervisors?.includes(u.id) || false} disabled={busy}
                           onChange={() => saveSettings({ supervisors: toggleSup(cfg.supervisors || [], u.id) })}
                           style={{ marginRight: 6 }} />
                    {u.username}
                  </label>
                ))}
              </div>}
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={label}>Report retention (days)</span>
          <input type="number" min={1} defaultValue={cfg.retention_days} disabled={busy}
                 onBlur={e => saveSettings({ retention_days: Number(e.target.value) })}
                 style={{ ...input, width: 90 }} />
        </div>
      </section>

      {/* ── Routes ── */}
      {!draft && (
        <section style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 18, margin: 0 }}>Patrol Routes</h2>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => navigate('/guard-patrol/test')} style={btn}>🎯 Camera Test Mode</button>
              <button onClick={startNew} style={primaryBtn}>+ Add Route</button>
            </div>
          </div>
          {(cfg.routes || []).length === 0
            ? <div style={{ ...card, color: 'var(--text-muted)' }}>No routes yet. Add one to create patrol checkpoints.</div>
            : (cfg.routes || []).map(rt => (
                <div key={rt.id} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>
                        {rt.name} {!rt.active && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(inactive)</span>}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                        {rt.schedule?.start}–{rt.schedule?.end} · every {rt.schedule?.frequency_minutes} min · {rt.checkpoints?.length || 0} checkpoint{(rt.checkpoints?.length || 0) === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <button onClick={() => startEdit(rt)} style={btn}>Edit</button>
                      <button onClick={() => deleteRoute(rt)} style={{ ...btn, color: 'var(--danger)' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
        </section>
      )}

      {/* ── Route editor ── */}
      {draft && (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18 }}>{editId ? 'Edit Route' : 'New Route'}</h2>

          <div style={card}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 240px' }}>
                <div style={label}>Route name</div>
                <input value={draft.name} onChange={e => setRoute({ name: e.target.value })} placeholder="e.g. Night Patrol" style={input} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={draft.active} onChange={e => setRoute({ active: e.target.checked })} /> Active
              </label>
            </div>

            {/* schedule */}
            <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Field label="Start"><input type="time" value={draft.schedule.start} onChange={e => setSched({ start: e.target.value })} style={input} /></Field>
              <Field label="End"><input type="time" value={draft.schedule.end} onChange={e => setSched({ end: e.target.value })} style={input} /></Field>
              <Field label="Every (min)"><input type="number" min={1} value={draft.schedule.frequency_minutes} onChange={e => setSched({ frequency_minutes: Number(e.target.value) })} style={{ ...input, width: 90 }} /></Field>
              <Field label="Early grace (s)"><input type="number" min={0} value={draft.schedule.early_window_seconds} onChange={e => setSched({ early_window_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
              <Field label="Late grace (s)"><input type="number" min={0} value={draft.schedule.late_window_seconds} onChange={e => setSched({ late_window_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={label}>Active days</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleDay(i)} style={chip(draft.schedule.active_days.includes(i))}>{d}</button>
                ))}
              </div>
            </div>

            {/* per-route supervisor override */}
            <div style={{ marginTop: 16 }}>
              <div style={label}>Supervisor override <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional — empty uses the global list)</span></div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {opts.supervisors.map(u => (
                  <label key={u.id} style={chip(draft.supervisor_override.includes(u.id))}>
                    <input type="checkbox" checked={draft.supervisor_override.includes(u.id)}
                           onChange={() => setRoute({ supervisor_override: toggleSup(draft.supervisor_override, u.id) })}
                           style={{ marginRight: 6 }} />
                    {u.username}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* checkpoints */}
          <h3 style={{ fontSize: 16, marginTop: 20 }}>Checkpoints</h3>
          {draft.checkpoints.map((cp, i) => (
            <div key={i} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b>Checkpoint {i + 1}</b>
                {draft.checkpoints.length > 1 && <button onClick={() => rmCp(i)} style={{ ...btn, color: 'var(--danger)' }}>Remove</button>}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                <Field label="Name"><input value={cp.name} onChange={e => setCp(i, { name: e.target.value })} style={input} /></Field>
                <Field label="Camera">
                  <select value={cp.camera_idx} onChange={e => setCp(i, { camera_idx: e.target.value })} style={input}>
                    <option value="">Select camera…</option>
                    {opts.cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Expected guard">
                  <select value={cp.expected_guard || ''} onChange={e => setCp(i, { expected_guard: e.target.value || null })} style={input}>
                    <option value="">Any / unassigned</option>
                    {opts.guards.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Min presence (s)"><input type="number" min={0} value={cp.min_presence_seconds} onChange={e => setCp(i, { min_presence_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
                <Field label="Hold gesture (s)"><input type="number" min={0} step="0.5" value={cp.gesture_hold_seconds} onChange={e => setCp(i, { gesture_hold_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
              </div>
              <div style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 13 }}>Check-in gesture: {GESTURE_LABEL[cp.gesture] || cp.gesture}</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={cp.face_checkin !== false} onChange={e => setCp(i, { face_checkin: e.target.checked })} />
                Auto check-in when the expected guard's face is recognized <span style={{ color: 'var(--text-muted)' }}>(face OR gesture; needs an expected guard set)</span>
              </label>

              <div style={{ marginTop: 14 }}>
                <div style={label}>Patrol zone {cp.camera_idx === '' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— select a camera first</span>}</div>
                {cp.camera_idx !== '' &&
                  <PatrolZoneEditor cameraIdx={cp.camera_idx} value={cp.patrol_zone} onChange={pts => setCp(i, { patrol_zone: pts })} />}
              </div>
            </div>
          ))}
          <button onClick={addCp} style={btn}>+ Add checkpoint</button>

          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button onClick={saveRoute} disabled={busy} style={primaryBtn}>{busy ? 'Saving…' : 'Save Route'}</button>
            <button onClick={cancel} disabled={busy} style={btn}>Cancel</button>
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const label = { fontSize: 13, fontWeight: 600, marginBottom: 8 };
const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn = { padding: '7px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const primaryBtn = { ...btn, background: 'var(--primary)', color: 'var(--primary-light)', border: 'none', fontWeight: 600 };
const chip = active => ({
  display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  background: active ? 'var(--primary-muted)' : 'transparent', color: 'var(--text)',
});
